import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { AppRouteBody } from '@/components/AppRouteBody';
import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { FeatureLock } from '@/components/ui/FeatureLock';
import { SearchBar } from '@/components/ui/SearchBar';
import { createContactSupportButton } from '@/components/ui/utils';
import { useAgentBackgroundPrefetch } from '@/hooks/useAgentBackgroundPrefetch';
import { Agent, Filter, useGetAgents } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import {
  AgentSetupModal,
  AgentsTableFilters,
} from '@/sections/agents/components';
import { AddCredentialModal } from '@/sections/agents/components/aegis-management/credentials/AddCredentialModal';
import { ProvisionTunnelModal } from '@/sections/agents/components/aegis-management/tunnel/ProvisionTunnelModal';
import { RemoveTunnelModal } from '@/sections/agents/components/aegis-management/tunnel/RemoveTunnelModal';
import { useTunnelManagement } from '@/sections/agents/components/aegis-management/tunnel/useTunnelManagement';
import { AddUserModal } from '@/sections/agents/components/aegis-management/user/AddUserModal';
import type { AgentDrawerTabId } from '@/sections/agents/components/drawer/types';
import {
  defaultColumns,
  getNoDataConfig,
  sortableColumnsConfig,
} from '@/sections/agents/config';
import { useAgentColumns } from '@/sections/agents/hooks/useAgentColumns';
import {
  AgentModalType,
  getAgentRowActions,
} from '@/sections/agents/utils/agentRowActions.util';
import { useDownloadInstaller } from '@/sections/agents/utils/downloadInstaller.util';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { useStorage } from '@/utils/storage/useStorage.util';

function AgentsComponent() {
  const { drawerState } = useGlobalState();
  const [filter, setFilter] = useStorage<Filter>(
    {
      queryKey: 'agentFilters',
    },
    {
      sort: {
        orderBy: 'fqdn',
        descending: true,
      },
    }
  );

  const [showNewAgentModal, setShowNewAgentModal] = useState(false);
  const { isPraetorianUser } = useAuth();
  const queryClient = useQueryClient();

  // Modal state for direct row action access (IA best practice)
  const [addUserModalAgent, setAddUserModalAgent] = useState<Agent | null>(
    null
  );
  const [addCredentialModalAgent, setAddCredentialModalAgent] =
    useState<Agent | null>(null);
  const [provisionTunnelModalAgent, setProvisionTunnelModalAgent] =
    useState<Agent | null>(null);
  const [removeTunnelModalAgent, setRemoveTunnelModalAgent] =
    useState<Agent | null>(null);

  // Tunnel management hook for getting tunnel info (needed for RemoveTunnelModal)
  const tunnelManagement = useTunnelManagement(!!removeTunnelModalAgent);

  // Handler to open drawer with a specific tab
  const handleOpenDrawerWithTab = useCallback(
    (agent: Agent, tab: AgentDrawerTabId) => {
      drawerState.handleOpenAgentDrawer(agent, { initialTab: tab });
    },
    [drawerState]
  );

  // Handler to open modals directly from row actions (IA best practice)
  const handleOpenModal = useCallback((modal: AgentModalType, agent: Agent) => {
    if (modal === 'addUser') {
      setAddUserModalAgent(agent);
    } else if (modal === 'addCredential') {
      setAddCredentialModalAgent(agent);
    } else if (modal === 'addTunnel') {
      setProvisionTunnelModalAgent(agent);
    } else if (modal === 'removeTunnel') {
      setRemoveTunnelModalAgent(agent);
    }
  }, []);

  const axios = useAxios();
  const downloadInstaller = useDownloadInstaller(axios);

  const {
    data: agents,
    status: agentsStatus,
    error: agentsError,
    isAllEmpty,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useGetAgents({ filter });

  // Start background prefetch after basic data loads
  // This populates individual agent caches for instant drawer opens
  useAgentBackgroundPrefetch(agents);

  // Detect initial loading state to prevent table UI flicker
  const isInitialLoading = agentsStatus === 'pending';

  const { activeColumns, setActiveColumns, columns } = useAgentColumns({
    key: 'agents',
    defaultColumns,
    defaultConfig: sortableColumnsConfig,
  });

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - filtering is client-side, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        status: filter.status,
        sort: filter.sort,
      }),
    [filter.status, filter.sort]
  );

  // Compute filter visibility - hide during loading, error, and empty states
  // to match other pages (Assets, Seeds, Vulnerabilities, Technologies, Integrations)
  // Error state also hides filters since NoData provides Retry/Setup actions
  const hasError = agentsStatus === 'error';
  const showFilters = !isInitialLoading && !isAllEmpty && !hasError;

  return (
    <>
      {showFilters && (
        <TableControlsContainer
          data-testid="agents-controls"
          filterLabel={true}
          filters={<AgentsTableFilters filter={filter} setFilter={setFilter} />}
          filtersRight={
            <SearchBar
              size="sm"
              className="h-fit w-64"
              placeholder="Search"
              value={filter?.search}
              onChange={value => {
                setFilter(prev => ({
                  ...prev,
                  search: value,
                }));
              }}
            />
          }
          selectionBar={
            <TableSelectionBar
              embedded={true}
              alwaysVisible={true}
              isVisible={false}
              selectedCount={0}
              totalCount={agents.length}
              hasNextPage={hasNextPage}
              resourceLabel="agents"
              emptySelectionMessage="Select agents to perform bulk actions"
              actions={
                <div className="flex items-center gap-3">
                  <h2 className="shrink-0 text-sm font-bold">Actions:</h2>
                  <Button
                    size="xxs"
                    styleType="rounded"
                    startIcon={<PlusCircleIcon className="size-4" />}
                    label="Add Agent"
                    onClick={() => setShowNewAgentModal(true)}
                    data-testid="agents-add-agent-button"
                  />
                </div>
              }
            />
          }
        />
      )}

      <div className="flex h-full flex-col overflow-hidden">
        {isInitialLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader isLoading type="spinner" />
          </div>
        ) : (
          <Table<Agent>
            key={filterKey}
            keyboardNavigation={true}
            sortableColumns={{
              activeColumns,
              setActiveColumns,
              defaultConfig: sortableColumnsConfig,
            }}
            sort={
              filter?.sort && {
                ...filter.sort,
                onSort: sort => {
                  setFilter(prev => ({ ...prev, sort }));
                },
              }
            }
            selection={{ hide: true }}
            rowHeight={64}
            name="agents"
            columns={columns}
            data={agents}
            error={agentsError}
            status={agentsStatus}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onRowClick={(agent: Agent) => {
              drawerState.handleOpenAgentDrawer(agent);
            }}
            rowActions={(agent: Agent) =>
              getAgentRowActions(
                agent,
                isPraetorianUser,
                queryClient,
                handleOpenDrawerWithTab,
                handleOpenModal
              )
            }
            noData={getNoDataConfig(
              isAllEmpty,
              agentsStatus === 'error',
              () => setShowNewAgentModal(true),
              () => handleRetry(),
              () =>
                setFilter({
                  sort: {
                    orderBy: 'fqdn',
                    descending: true,
                  },
                })
            )}
          />
        )}
      </div>

      <AgentSetupModal
        open={showNewAgentModal}
        onClose={() => setShowNewAgentModal(false)}
        onDownloadInstaller={async type => {
          await downloadInstaller.mutateAsync(type);
        }}
      />

      {/* Direct modal access from row actions (IA best practice) */}
      {addUserModalAgent && (
        <AddUserModal
          isOpen={!!addUserModalAgent}
          onClose={() => setAddUserModalAgent(null)}
          agent={addUserModalAgent}
          onSuccess={taskId => {
            // Open drawer to tasks tab after successful user creation
            if (taskId && addUserModalAgent) {
              drawerState.handleOpenAgentDrawer(addUserModalAgent, {
                initialTab: 'tasks',
              });
            }
          }}
        />
      )}

      {addCredentialModalAgent && (
        <AddCredentialModal
          isOpen={!!addCredentialModalAgent}
          onClose={() => setAddCredentialModalAgent(null)}
          agent={addCredentialModalAgent}
        />
      )}

      {provisionTunnelModalAgent && (
        <ProvisionTunnelModal
          isOpen={!!provisionTunnelModalAgent}
          onClose={() => setProvisionTunnelModalAgent(null)}
          agent={provisionTunnelModalAgent}
          onSuccess={taskId => {
            // Open drawer to tasks tab after successful tunnel provisioning
            if (taskId && provisionTunnelModalAgent) {
              drawerState.handleOpenAgentDrawer(provisionTunnelModalAgent, {
                initialTab: 'tasks',
              });
            }
          }}
        />
      )}

      {removeTunnelModalAgent && (
        <RemoveTunnelModal
          isOpen={!!removeTunnelModalAgent}
          onClose={() => setRemoveTunnelModalAgent(null)}
          agent={removeTunnelModalAgent}
          tunnelHostname={
            tunnelManagement.getTunnelInfo(removeTunnelModalAgent).hostname ||
            ''
          }
          tunnelName={
            tunnelManagement.getTunnelInfo(removeTunnelModalAgent).tunnelName
          }
          onSuccess={taskId => {
            // Open drawer to tasks tab after successful tunnel removal
            if (taskId && removeTunnelModalAgent) {
              drawerState.handleOpenAgentDrawer(removeTunnelModalAgent, {
                initialTab: 'tasks',
              });
            }
          }}
        />
      )}
    </>
  );
}

export function Agents() {
  const {
    locked,
    lockTitle,
    lockDescription,
    featureName,
    isLoading: featureLockLoading,
  } = useFeatureLock('agents');

  return (
    <AppRouteBody title="Agents">
      {featureLockLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader isLoading type="spinner" />
        </div>
      ) : (
        <FeatureLock
          locked={locked}
          title={lockTitle}
          description={lockDescription}
          action={createContactSupportButton(featureName)}
        >
          <AgentsComponent />
        </FeatureLock>
      )}
    </AppRouteBody>
  );
}
